import { useCallback, useEffect, useMemo, useState } from "react";
import {
  getCharacterizationContainersList,
  getSuperContainerList,
} from "../../../services/container";
import { sortByImageUrl } from "../../../shared/utils/utils";

const toSiteId = (value) => {
  if (value == null || value === "") return "";
  return String(value);
};

export const buildSites = (allContainers, superContainers) => {
  if (!Array.isArray(allContainers) || !Array.isArray(superContainers)) {
    return [];
  }
  const containersById = new Map(
    allContainers
      .map((container) => {
        const id = toSiteId(container?._id);
        return id ? [id, container] : null;
      })
      .filter(Boolean),
  );
  const idsGroupedInSuper = new Set(
    superContainers.flatMap((superContainer) =>
      (superContainer?.containerObjectIdList ?? [])
        .map(toSiteId)
        .filter(Boolean),
    ),
  );
  const singleSites = allContainers
    .filter((container) => !idsGroupedInSuper.has(toSiteId(container._id)))
    .map((container) => ({
      ...container,
      isSuperContainer: false,
    }));
  const superSites = superContainers
    .map((superContainer) => {
      const linkedIds = (superContainer?.containerObjectIdList ?? [])
        .map(toSiteId)
        .filter(Boolean);
      const linkedContainers = linkedIds
        .map((id) => containersById.get(id))
        .filter(Boolean);
      if (!linkedContainers.length) {
        return null;
      }
      const representative =
        linkedContainers.length === 1
          ? linkedContainers[0]
          : [...linkedContainers].sort(sortByImageUrl)[0];
      return {
        ...representative,
        isSuperContainer: true,
        superContainerId: superContainer._id,
        superContainerPublicId: superContainer.superContainerId,
        linkedContainerIds: linkedIds,
        linkedContainers,
      };
    })
    .filter(Boolean);
  return [...singleSites, ...superSites].sort(sortByImageUrl);
};
export const useCharacterizationSites = () => {
  const [superContainers, setSuperContainers] = useState([]);
  const [allContainers, setAllContainers] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const fetchData = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const [superResponse, containersResponse] = await Promise.all([
        getSuperContainerList(),
        getCharacterizationContainersList({ characterizationOnly: false }),
      ]);
      if (containersResponse.status !== 200) {
        throw new Error("Unable to load containers");
      }
      setSuperContainers(superResponse?.data?.superContainers ?? []);
      setAllContainers(containersResponse?.data?.data ?? []);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("Failed to fetch characterization sites", err);
      setError(err);
    } finally {
      setIsLoading(false);
    }
  }, []);
  useEffect(() => {
    fetchData();
  }, [fetchData]);
  const sites = useMemo(
    () => buildSites(allContainers, superContainers),
    [allContainers, superContainers],
  );
  return {
    sites,
    superContainers,
    isLoading,
    error,
    reload: fetchData,
    containers: allContainers,
  };
};
